/**
 * FineIndex SQLite Database Layer
 * 
 * SQLite-backed implementation of IFineIndexDB for Electron/Node.js.
 * Uses better-sqlite3 via the preload bridge (`window.nodeSQLite`).
 * 
 * Designed for filesystem-based projects where the database
 * lives alongside project files on disk.
 * 
 * @module @engine/aid/FineIndexSQLite
 */

import type {
  AssetEntry,
  AssetQuery,
  AssetQueryResult,
  IndexStats,
  ProgressCallback,
  IFineIndexDB,
} from './types';

// ============================================
// PRELOAD BRIDGE TYPES
// ============================================

interface SQLiteRunResult {
  changes: number;
  lastInsertRowid: number;
}

interface NodeSQLiteAPI {
  open(dbPath: string): void;
  close(dbPath: string): void;
  exec(dbPath: string, sql: string): void;
  run(dbPath: string, sql: string, params?: unknown[]): SQLiteRunResult;
  get(dbPath: string, sql: string, params?: unknown[]): Record<string, unknown> | undefined;
  all(dbPath: string, sql: string, params?: unknown[]): Record<string, unknown>[];
}

declare global {
  interface Window {
    nodeSQLite?: NodeSQLiteAPI;
  }
}

// ============================================
// HELPERS
// ============================================

function getSQLiteAPI(): NodeSQLiteAPI {
  if (!window.nodeSQLite) {
    throw new Error('[FineIndexSQLite] nodeSQLite API not available. Are you running in Electron?');
  }
  return window.nodeSQLite;
}

export function isNodeSQLiteSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.nodeSQLite !== 'undefined';
}

// ============================================
// RECORD CONVERSION
// ============================================

interface AssetRow {
  guid: string;
  path: string;
  contentType: string;
  importerType: string;
  importerParams: string;
  size: number;
  contentHash: string;
  createdAt: number;
  updatedAt: number;
  fileModifiedAt: number;
  labels: string;
  importStatus: string;
  importError: string | null;
  artifacts: string | null;
  dependencies: string | null;
  importedData: string | null;
  producedAssets: string | null;
  sourceGuid: string | null;
}

function entryToRow(entry: AssetEntry): AssetRow {
  const producedAssetsSerialized = entry.producedAssets?.map(p => ({
    ...p,
    producedAt: p.producedAt instanceof Date ? p.producedAt.getTime() : p.producedAt,
  }));

  return {
    guid: entry.guid,
    path: entry.path,
    contentType: entry.contentType,
    importerType: entry.importerType,
    importerParams: JSON.stringify(entry.importerParams),
    size: entry.size,
    contentHash: entry.contentHash,
    createdAt: entry.createdAt.getTime(),
    updatedAt: entry.updatedAt.getTime(),
    fileModifiedAt: entry.fileModifiedAt.getTime(),
    labels: JSON.stringify(entry.labels),
    importStatus: entry.importStatus,
    importError: entry.importError ?? null,
    artifacts: entry.artifacts ? JSON.stringify(entry.artifacts) : null,
    dependencies: entry.dependencies ? JSON.stringify(entry.dependencies) : null,
    importedData: entry.importedData ? JSON.stringify(entry.importedData) : null,
    producedAssets: producedAssetsSerialized ? JSON.stringify(producedAssetsSerialized) : null,
    sourceGuid: entry.sourceGuid ?? null,
  };
}

function rowToEntry(row: Record<string, unknown>): AssetEntry {
  const r = row as unknown as AssetRow;

  const producedAssetsRaw = r.producedAssets ? JSON.parse(r.producedAssets) : undefined;
  const producedAssets = producedAssetsRaw?.map((p: { producedAt: number | string }) => ({
    ...p,
    producedAt: new Date(p.producedAt),
  }));

  return {
    guid: r.guid,
    path: r.path,
    contentType: r.contentType,
    importerType: r.importerType,
    importerParams: JSON.parse(r.importerParams),
    size: r.size,
    contentHash: r.contentHash,
    createdAt: new Date(r.createdAt),
    updatedAt: new Date(r.updatedAt),
    fileModifiedAt: new Date(r.fileModifiedAt),
    labels: JSON.parse(r.labels),
    importStatus: r.importStatus as AssetEntry['importStatus'],
    importError: r.importError ?? undefined,
    artifacts: r.artifacts ? JSON.parse(r.artifacts) : undefined,
    dependencies: r.dependencies ? JSON.parse(r.dependencies) : undefined,
    importedData: r.importedData ? JSON.parse(r.importedData) : undefined,
    producedAssets,
    sourceGuid: r.sourceGuid ?? undefined,
  };
}

// ============================================
// SQLITE DATABASE WRAPPER
// ============================================

export class FineIndexSQLite implements IFineIndexDB {
  private dbPath: string;
  private api: NodeSQLiteAPI | null = null;
  private ready = false;

  // In-memory caches (same as FineIndexDB)
  private guidToPath = new Map<string, string>();
  private pathToGuid = new Map<string, string>();

  constructor(dbPath: string) {
    this.dbPath = dbPath;
  }

  // ============================================
  // LIFECYCLE
  // ============================================

  async init(): Promise<void> {
    if (this.ready) return;

    this.api = getSQLiteAPI();
    this.api.open(this.dbPath);

    // Create tables + indexes
    this.api.exec(this.dbPath, `
      CREATE TABLE IF NOT EXISTS assets (
        guid          TEXT PRIMARY KEY,
        path          TEXT NOT NULL UNIQUE,
        contentType   TEXT NOT NULL,
        importerType  TEXT NOT NULL,
        importerParams TEXT NOT NULL,
        size          INTEGER NOT NULL,
        contentHash   TEXT NOT NULL,
        createdAt     INTEGER NOT NULL,
        updatedAt     INTEGER NOT NULL,
        fileModifiedAt INTEGER NOT NULL,
        labels        TEXT NOT NULL,
        importStatus  TEXT NOT NULL DEFAULT 'pending',
        importError   TEXT,
        artifacts     TEXT,
        dependencies  TEXT,
        importedData  TEXT,
        producedAssets TEXT,
        sourceGuid    TEXT
      );

      CREATE INDEX IF NOT EXISTS idx_assets_path ON assets(path);
      CREATE INDEX IF NOT EXISTS idx_assets_contentType ON assets(contentType);
      CREATE INDEX IF NOT EXISTS idx_assets_importerType ON assets(importerType);
      CREATE INDEX IF NOT EXISTS idx_assets_updatedAt ON assets(updatedAt);
      CREATE INDEX IF NOT EXISTS idx_assets_sourceGuid ON assets(sourceGuid);
      CREATE INDEX IF NOT EXISTS idx_assets_ct_updated ON assets(contentType, updatedAt);
      CREATE INDEX IF NOT EXISTS idx_assets_it_updated ON assets(importerType, updatedAt);

      CREATE TABLE IF NOT EXISTS meta (
        key   TEXT PRIMARY KEY,
        value TEXT NOT NULL
      );
    `);

    // Enable WAL mode for better concurrent read performance
    this.api.exec(this.dbPath, 'PRAGMA journal_mode = WAL;');
    this.api.exec(this.dbPath, 'PRAGMA synchronous = NORMAL;');

    this.ready = true;
    await this.rebuildCache();
  }

  async close(): Promise<void> {
    if (!this.ready) return;

    this.api!.close(this.dbPath);
    this.api = null;
    this.ready = false;
    this.clearCache();
  }

  isReady(): boolean {
    return this.ready;
  }

  private ensureReady(): void {
    if (!this.ready || !this.api) {
      throw new Error('FineIndexSQLite not initialized. Call init() first.');
    }
  }

  // ============================================
  // CACHE MANAGEMENT
  // ============================================

  private clearCache(): void {
    this.guidToPath.clear();
    this.pathToGuid.clear();
  }

  private async rebuildCache(): Promise<void> {
    this.ensureReady();
    this.clearCache();

    const rows = this.api!.all(this.dbPath, 'SELECT guid, path FROM assets');
    for (const row of rows) {
      const guid = row['guid'] as string;
      const path = row['path'] as string;
      this.guidToPath.set(guid, path);
      this.pathToGuid.set(path, guid);
    }
  }

  private updateCache(entry: AssetEntry): void {
    const oldPath = this.guidToPath.get(entry.guid);
    if (oldPath && oldPath !== entry.path) {
      this.pathToGuid.delete(oldPath);
    }
    const oldGuid = this.pathToGuid.get(entry.path);
    if (oldGuid && oldGuid !== entry.guid) {
      this.guidToPath.delete(oldGuid);
    }
    this.guidToPath.set(entry.guid, entry.path);
    this.pathToGuid.set(entry.path, entry.guid);
  }

  private removeFromCache(guid: string): void {
    const path = this.guidToPath.get(guid);
    if (path) this.pathToGuid.delete(path);
    this.guidToPath.delete(guid);
  }

  // ============================================
  // FAST LOOKUPS (from cache)
  // ============================================

  getGuidForPath(path: string): string | null {
    return this.pathToGuid.get(path) ?? null;
  }

  getPathForGuid(guid: string): string | null {
    return this.guidToPath.get(guid) ?? null;
  }

  hasPath(path: string): boolean {
    return this.pathToGuid.has(path);
  }

  hasGuid(guid: string): boolean {
    return this.guidToPath.has(guid);
  }

  getAllPaths(): string[] {
    return Array.from(this.pathToGuid.keys());
  }

  getAllGuids(): string[] {
    return Array.from(this.guidToPath.keys());
  }

  // ============================================
  // SINGLE OPERATIONS
  // ============================================

  async getByGuid(guid: string): Promise<AssetEntry | null> {
    this.ensureReady();
    const row = this.api!.get(this.dbPath, 'SELECT * FROM assets WHERE guid = ?', [guid]);
    return row ? rowToEntry(row) : null;
  }

  async getByPath(path: string): Promise<AssetEntry | null> {
    this.ensureReady();
    const row = this.api!.get(this.dbPath, 'SELECT * FROM assets WHERE path = ?', [path]);
    return row ? rowToEntry(row) : null;
  }

  async getBySourceGuid(sourceGuid: string): Promise<AssetEntry[]> {
    this.ensureReady();
    const rows = this.api!.all(this.dbPath, 'SELECT * FROM assets WHERE sourceGuid = ?', [sourceGuid]);
    return rows.map(rowToEntry);
  }

  async put(entry: AssetEntry): Promise<void> {
    this.ensureReady();
    const r = entryToRow(entry);

    this.api!.run(this.dbPath, `
      INSERT OR REPLACE INTO assets
        (guid, path, contentType, importerType, importerParams, size, contentHash,
         createdAt, updatedAt, fileModifiedAt, labels, importStatus, importError,
         artifacts, dependencies, importedData, producedAssets, sourceGuid)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `, [
      r.guid, r.path, r.contentType, r.importerType, r.importerParams,
      r.size, r.contentHash, r.createdAt, r.updatedAt, r.fileModifiedAt,
      r.labels, r.importStatus, r.importError, r.artifacts, r.dependencies,
      r.importedData, r.producedAssets, r.sourceGuid,
    ]);

    this.updateCache(entry);
  }

  async deleteByGuid(guid: string): Promise<boolean> {
    this.ensureReady();
    const existed = this.hasGuid(guid);
    if (existed) {
      this.api!.run(this.dbPath, 'DELETE FROM assets WHERE guid = ?', [guid]);
      this.removeFromCache(guid);
    }
    return existed;
  }

  async deleteByPath(path: string): Promise<boolean> {
    this.ensureReady();
    const guid = this.getGuidForPath(path);
    if (guid) {
      return this.deleteByGuid(guid);
    }
    return false;
  }

  async deleteAllProducedEntries(): Promise<number> {
    this.ensureReady();

    // Get the guids first so we can update cache
    const rows = this.api!.all(
      this.dbPath,
      "SELECT guid FROM assets WHERE sourceGuid IS NOT NULL AND sourceGuid != ''",
    );
    const guids = rows.map(r => r['guid'] as string);

    if (guids.length > 0) {
      this.api!.run(
        this.dbPath,
        "DELETE FROM assets WHERE sourceGuid IS NOT NULL AND sourceGuid != ''",
      );
      for (const guid of guids) {
        this.removeFromCache(guid);
      }
    }

    return guids.length;
  }

  // ============================================
  // BULK OPERATIONS
  // ============================================

  async bulkPut(entries: AssetEntry[], onProgress?: ProgressCallback): Promise<void> {
    this.ensureReady();
    if (entries.length === 0) return;

    const BATCH_SIZE = 500;

    for (let i = 0; i < entries.length; i += BATCH_SIZE) {
      const batch = entries.slice(i, i + BATCH_SIZE);

      // Use a transaction for each batch
      this.api!.exec(this.dbPath, 'BEGIN');
      try {
        for (const entry of batch) {
          const r = entryToRow(entry);
          this.api!.run(this.dbPath, `
            INSERT OR REPLACE INTO assets
              (guid, path, contentType, importerType, importerParams, size, contentHash,
               createdAt, updatedAt, fileModifiedAt, labels, importStatus, importError,
               artifacts, dependencies, importedData, producedAssets, sourceGuid)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
          `, [
            r.guid, r.path, r.contentType, r.importerType, r.importerParams,
            r.size, r.contentHash, r.createdAt, r.updatedAt, r.fileModifiedAt,
            r.labels, r.importStatus, r.importError, r.artifacts, r.dependencies,
            r.importedData, r.producedAssets, r.sourceGuid,
          ]);
          this.updateCache(entry);
        }
        this.api!.exec(this.dbPath, 'COMMIT');
      } catch (e) {
        this.api!.exec(this.dbPath, 'ROLLBACK');
        throw e;
      }

      if (onProgress) {
        onProgress({
          current: Math.min(i + BATCH_SIZE, entries.length),
          total: entries.length,
          phase: 'updating',
        });
      }
    }
  }

  async bulkDelete(guids: string[], onProgress?: ProgressCallback): Promise<void> {
    this.ensureReady();
    if (guids.length === 0) return;

    const BATCH_SIZE = 500;

    for (let i = 0; i < guids.length; i += BATCH_SIZE) {
      const batch = guids.slice(i, i + BATCH_SIZE);

      this.api!.exec(this.dbPath, 'BEGIN');
      try {
        for (const guid of batch) {
          this.api!.run(this.dbPath, 'DELETE FROM assets WHERE guid = ?', [guid]);
          this.removeFromCache(guid);
        }
        this.api!.exec(this.dbPath, 'COMMIT');
      } catch (e) {
        this.api!.exec(this.dbPath, 'ROLLBACK');
        throw e;
      }

      if (onProgress) {
        onProgress({
          current: Math.min(i + BATCH_SIZE, guids.length),
          total: guids.length,
          phase: 'updating',
        });
      }
    }
  }

  // ============================================
  // QUERIES
  // ============================================

  async getByContentType(contentType: string): Promise<AssetEntry[]> {
    this.ensureReady();
    const rows = this.api!.all(
      this.dbPath,
      'SELECT * FROM assets WHERE contentType = ?',
      [contentType],
    );
    return rows.map(rowToEntry);
  }

  async getByImporterType(importerType: string): Promise<AssetEntry[]> {
    this.ensureReady();
    const rows = this.api!.all(
      this.dbPath,
      'SELECT * FROM assets WHERE importerType = ?',
      [importerType],
    );
    return rows.map(rowToEntry);
  }

  async query(query: AssetQuery): Promise<AssetQueryResult> {
    this.ensureReady();

    const conditions: string[] = [];
    const params: unknown[] = [];

    if (query.contentType) {
      const types = Array.isArray(query.contentType) ? query.contentType : [query.contentType];
      conditions.push(`contentType IN (${types.map(() => '?').join(',')})`);
      params.push(...types);
    }

    if (query.importerType) {
      const types = Array.isArray(query.importerType) ? query.importerType : [query.importerType];
      conditions.push(`importerType IN (${types.map(() => '?').join(',')})`);
      params.push(...types);
    }

    if (query.pathPrefix) {
      const prefix = query.pathPrefix.endsWith('/') ? query.pathPrefix : query.pathPrefix + '/';
      conditions.push('(path LIKE ? OR path = ?)');
      params.push(prefix + '%', query.pathPrefix);
    }

    const where = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '';

    // Get all matching rows (labels filter done in-memory like FineIndexDB)
    let rows = this.api!.all(this.dbPath, `SELECT * FROM assets ${where}`, params);

    // Apply labels filter in memory
    if (query.labels && query.labels.length > 0) {
      const labelSet = new Set(query.labels);
      rows = rows.filter(r => {
        const labels: string[] = JSON.parse(r['labels'] as string);
        return labels.some(l => labelSet.has(l));
      });
    }

    const totalCount = rows.length;

    // Sort
    if (query.sortBy) {
      const dir = query.sortOrder === 'desc' ? -1 : 1;
      rows.sort((a, b) => {
        const key = query.sortBy!;
        const aVal = a[key] as string | number;
        const bVal = b[key] as string | number;
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }

    // Pagination
    if (query.offset) rows = rows.slice(query.offset);
    if (query.limit) rows = rows.slice(0, query.limit);

    return {
      entries: rows.map(rowToEntry),
      totalCount,
    };
  }

  // ============================================
  // STATISTICS
  // ============================================

  async count(): Promise<number> {
    this.ensureReady();
    const row = this.api!.get(this.dbPath, 'SELECT COUNT(*) as cnt FROM assets');
    return (row?.['cnt'] as number) ?? 0;
  }

  async getStats(): Promise<IndexStats> {
    this.ensureReady();

    const rows = this.api!.all(this.dbPath, 'SELECT contentType, importerType, size FROM assets');

    const byContentType: Record<string, number> = {};
    const byImporterType: Record<string, number> = {};
    let totalSize = 0;

    for (const row of rows) {
      const ct = row['contentType'] as string;
      const it = row['importerType'] as string;
      byContentType[ct] = (byContentType[ct] || 0) + 1;
      byImporterType[it] = (byImporterType[it] || 0) + 1;
      totalSize += row['size'] as number;
    }

    const lastRefreshStr = await this.getMeta('lastRefresh');
    const lastRefresh = lastRefreshStr ? new Date(parseInt(lastRefreshStr, 10)) : null;

    return {
      totalAssets: rows.length,
      byContentType,
      byImporterType,
      totalSize,
      lastRefresh,
    };
  }

  // ============================================
  // META
  // ============================================

  async getMeta(key: string): Promise<string | null> {
    this.ensureReady();
    const row = this.api!.get(this.dbPath, 'SELECT value FROM meta WHERE key = ?', [key]);
    return (row?.['value'] as string) ?? null;
  }

  async setMeta(key: string, value: string): Promise<void> {
    this.ensureReady();
    this.api!.run(
      this.dbPath,
      'INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)',
      [key, value],
    );
  }

  async setLastRefresh(date: Date): Promise<void> {
    await this.setMeta('lastRefresh', date.getTime().toString());
  }

  // ============================================
  // CLEAR
  // ============================================

  async clear(): Promise<void> {
    this.ensureReady();
    this.api!.exec(this.dbPath, 'DELETE FROM assets');
    this.api!.exec(this.dbPath, 'DELETE FROM meta');
    this.clearCache();
  }
}

// ============================================
// DELETE DATABASE
// ============================================

